import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt, fmtDate, weekdayShort, BILL_CATEGORIES, RECURRENCE, catColor, catLabel, downloadCSV } from "@/lib/constants";
import { Plus, Pencil, Trash2, Check, Download, Search, Lock, Link2, CalendarClock, Zap, Receipt, ChevronDown, CreditCard } from "lucide-react";
import { Link } from "react-router-dom";
import BillModal from "@/components/BillModal";
import PayoffCelebration from "@/components/debts/PayoffCelebration";
import GuidedEmpty from "@/components/GuidedEmpty";
import { toast } from "sonner";

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("all");
  const [celebrate, setCelebrate] = useState(null);
  const [showIdle, setShowIdle] = useState(false);

  const load = () => api.get("/bills").then(r => setBills(r.data));
  useEffect(() => { load(); }, []);

  const filtered = bills
    .filter(b => !(b.debt_category === "credit_card" && (b.debt_balance ?? 0) <= 0))
    .filter(b => tab === "all" ? true : tab === "paid" ? b.paid : !b.paid)
    .filter(b => !q || b.name.toLowerCase().includes(q.toLowerCase()));

  const idleCards = bills.filter(b => b.debt_category === "credit_card" && (b.debt_balance ?? 0) <= 0);

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.name}"?`)) return;
    try { await api.delete(`/bills/${b.id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };
  const pay = async (b) => {
    try {
      const planned = b.planned_amount != null && b.planned_amount > b.amount ? b.planned_amount : null;
      const { data } = await api.post(`/bills/${b.id}/pay`, planned ? { amount: planned } : undefined);
      const dp = data.debt_payment;
      if (dp?.paid_off) setCelebrate(dp);
      else if (dp) {
        toast.success(`Paid ${fmt(dp.payment)} on ${dp.debt_name}`, {
          description: `${fmt(dp.interest)} interest · ${fmt(dp.principal)} principal · balance now ${fmt(dp.balance_after)}`,
        });
      } else toast.success("Marked paid");
      load();
    } catch { toast.error("Failed"); }
  };
  const exportCSV = () => {
    downloadCSV("oweverit-bills.csv", bills.map(b => ({
      name: b.name, amount: b.amount, planned_amount: b.planned_amount ?? b.amount, minimum_payment: b.minimum_payment, due_date: b.due_date,
      interest_rate: b.interest_rate, category: b.category, recurrence: b.recurrence, autopay: b.autopay, paid: b.paid,
    })));
  };

  return (
    <div className="space-y-5" data-testid="bills-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-teal-500 font-semibold">Bill Ledger</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Bills</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition" data-testid="bills-export">
            <Download className="h-4 w-4" /> CSV
          </button>
          <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="bills-add-btn"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition">
            <Plus className="h-4 w-4" /> Add Bill
          </button>
        </div>
      </div>

      {bills.length === 0 ? (
        <GuidedEmpty
          testid="bills-empty"
          tone="teal"
          icon={Receipt}
          title="Add your first bill"
          subtitle="Track rent, utilities, subscriptions — anything with a due date. OWEverit puts them on your calendar and reminds you before they're due."
          tips={[
            "Tag autopay bills so reminders stay quiet for those.",
            "Loans and credit cards get a bill automatically when you add them on the Debts page.",
            "Recurring bills roll forward each cycle until you remove them.",
          ]}
          primary={{ label: "Add a bill", onClick: () => { setEditing(null); setOpen(true); }, testid: "bills-empty-add" }}
          secondary={[{ label: "Add a card or loan", to: "/debts?coach=1", testid: "bills-empty-debts" }]}
          note="Takes about a minute."
        />
      ) : (
        <>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search bills…"
            className="pl-9 pr-3 py-2 rounded-lg bg-card border border-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 w-64" />
        </div>
        <div className="inline-flex bg-accent p-1 rounded-lg">
          {["all", "unpaid", "paid"].map(t => (
            <button key={t} onClick={() => setTab(t)} data-testid={`bills-tab-${t}`}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold capitalize transition ${
                tab === t ? "bg-card shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"
              }`}>{t}</button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto thin-scroll">
          <table className="w-full text-sm">
            <thead className="bg-accent/40 border-b border-border">
              <tr className="text-left text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                <th className="p-3">Name</th>
                <th className="p-3">Category</th>
                <th className="p-3">Due</th>
                <th className="p-3">Recurrence</th>
                <th className="p-3 text-right">Amount (per plan)</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">No bills. Click "Add Bill" or load demo data.</td></tr>
              )}
              {filtered.map(b => (
                <tr key={b.id} data-testid={`bill-row-${b.id}`} className="border-b border-border/60 hover:bg-accent/30">
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <span className="h-2 w-2 rounded-full" style={{ background: catColor(BILL_CATEGORIES, b.category) }} />
                      <div>
                        <div className={`font-semibold ${b.paid ? "line-through text-muted-foreground" : ""} flex items-center gap-1.5`}>
                          {b.name}
                          {b.debt_id && (
                            <Link to="/debts" data-testid={`bill-debt-badge-${b.id}`} title="Linked to a debt — minimum payment"
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 hover:bg-indigo-500/25 transition">
                              <Link2 className="h-2.5 w-2.5" /> Debt
                            </Link>
                          )}
                          {b.autopay && (
                            <span data-testid={`bill-autopay-badge-${b.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-teal-500/15 text-teal-500 border border-teal-500/30">
                              <Lock className="h-2.5 w-2.5" /> Autopay
                            </span>
                          )}
                        </div>
                        {b.interest_rate > 0 && <div className="text-[10px] text-muted-foreground">{b.interest_rate}% APR</div>}
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs">{catLabel(BILL_CATEGORIES, b.category)}</td>
                  <td className="p-3 font-mono text-xs">
                    {fmtDate(b.pay_date || b.due_date)}
                    {b.weekend_shifted && (
                      <div className="text-[10px] text-amber-500 font-sans flex items-center gap-1" data-testid={`bill-weekend-shift-${b.id}`}>
                        <CalendarClock className="h-3 w-3" /> moved from {weekdayShort(b.due_date)} {fmtDate(b.due_date)}{b.shift_reason && b.shift_reason !== "weekend" ? ` (${b.shift_reason})` : ""}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-xs capitalize">{RECURRENCE.find(r => r.key === b.recurrence)?.label || b.recurrence}</td>
                  <td className="p-3 text-right font-mono font-semibold">
                    {b.planned_amount != null && b.planned_amount > b.amount ? (
                      <div data-testid={`bill-planned-${b.id}`}>
                        <div className="text-indigo-400 flex items-center justify-end gap-1"><Zap className="h-3 w-3" /> {fmt(b.planned_amount)}</div>
                        <div className="text-[10px] text-muted-foreground font-sans font-normal">
                          min {fmt(b.amount)}{b.planned_extra > 0 && ` + ${fmt(b.planned_extra)} snowball`}{b.planned_boost > 0 && ` + ${fmt(b.planned_boost)} util boost`}
                        </div>
                      </div>
                    ) : fmt(b.amount)}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-1">
                      {!b.paid && (
                        <button onClick={() => pay(b)} title="Mark paid" data-testid={`bill-pay-${b.id}`}
                          className="p-1.5 rounded-md hover:bg-emerald-500/10 text-emerald-500 transition"><Check className="h-4 w-4" /></button>
                      )}
                      <button onClick={() => { setEditing(b); setOpen(true); }} title="Edit" data-testid={`bill-edit-${b.id}`}
                        className="p-1.5 rounded-md hover:bg-accent transition"><Pencil className="h-4 w-4" /></button>
                      {b.debt_id ? (
                        <span title="Linked to a debt — delete the debt to remove" data-testid={`bill-delete-locked-${b.id}`}
                          className="p-1.5 rounded-md text-muted-foreground/40 cursor-not-allowed"><Trash2 className="h-4 w-4" /></span>
                      ) : (
                        <button onClick={() => remove(b)} title="Delete" data-testid={`bill-delete-${b.id}`}
                          className="p-1.5 rounded-md hover:bg-rose-500/10 text-rose-500 transition"><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {idleCards.length > 0 && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="idle-cards-section">
          <button onClick={() => setShowIdle(s => !s)} data-testid="idle-cards-toggle"
            className="w-full flex items-center gap-2 p-3 text-left hover:bg-accent/40 transition">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-semibold">Paid-off &amp; idle cards</span>
            <span className="text-xs text-muted-foreground">({idleCards.length})</span>
            <span className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{showIdle ? "Hide" : "Show"}</span>
            <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${showIdle ? "rotate-180" : ""}`} />
          </button>
          {showIdle && (
            <div className="border-t border-border divide-y divide-border/60" data-testid="idle-cards-list">
              <div className="px-3 py-2 text-[11px] text-muted-foreground bg-accent/20">
                Zero-balance cards with nothing due. They're kept out of your bill list and payoff plan, but still count toward your credit utilization.
              </div>
              {idleCards.map(b => (
                <div key={b.id} data-testid={`idle-card-${b.id}`} className="flex items-center gap-3 p-3 text-sm">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: catColor(BILL_CATEGORIES, b.category) }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{b.name}</div>
                    <div className="text-[10px] text-muted-foreground">$0 balance{b.interest_rate > 0 ? ` · ${b.interest_rate}% APR` : ""}</div>
                  </div>
                  <Link to="/debts" data-testid={`idle-card-manage-${b.id}`}
                    className="text-xs font-medium text-teal-500 hover:underline shrink-0">Manage</Link>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
        </>
      )}
      <BillModal open={open} onClose={() => setOpen(false)} bill={editing} onSaved={load} />
      <PayoffCelebration payment={celebrate} onClose={() => setCelebrate(null)} />
    </div>
  );
}
